"use client";
import { PhoneInput } from "@/components/PhoneInput";
import { Spinner } from "@/components/Spinner";
import { valibotResolver } from "@hookform/resolvers/valibot";
import { useAction } from "next-safe-action/hooks";
import Link from "next/link";
import { type SubmitHandler, useForm } from "react-hook-form";
import { Dropdown, Input, Textarea } from "ui";
import { PrimaryButton } from "ui/components";
import { type SendQuestionInput, type SendQuestionOutput, SendQuestionSchema } from "./schema";
import type { sendQuestionAction } from "./sendQuestionAction";

type Props = {
  sendQuestionAction: typeof sendQuestionAction;
};
export const ContactUsForm = ({ sendQuestionAction }: Props) => {
  const {
    control,
    handleSubmit,
    reset: resetForm,
  } = useForm<SendQuestionInput, unknown, SendQuestionOutput>({
    resolver: valibotResolver(SendQuestionSchema),
    defaultValues: {
      email: "",
      firstname: "",
      lastname: "",
      phone: undefined,
      subject: "Une réservation",
      message: "",
    } satisfies SendQuestionInput,
  });

  const { execute, status, result, reset } = useAction(sendQuestionAction, {
    onSuccess: () => {
      resetForm({
        email: "",
        firstname: "",
        lastname: "",
        phone: undefined,
        subject: "Une réservation",
        message: "",
      } satisfies SendQuestionInput);
      setTimeout(() => reset(), 2000);
    },
  });

  const onSubmit: SubmitHandler<SendQuestionOutput> = (data) => {
    execute(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col space-y-4">
      <Input id="lastname" label="Nom*" name="lastname" control={control} />
      <Input id="firstname" label="Prénom*" name="firstname" control={control} />
      <Input id="email" label="Adresse e-mail*" name="email" control={control} />
      <PhoneInput id="tel-national" label="Téléphone" name="phone" control={control} />
      <Dropdown
        id="subject"
        label={"J'ai une question à propos de*"}
        name="subject"
        control={control}
        items={[
          { text: "Une réservation", callbackValue: "Une réservation" },
          { text: "Un voyage ou un départ", callbackValue: "Un voyage ou un départ" },
          { text: "Un avoir ou un remboursement", callbackValue: "Un avoir ou un remboursement" },
          {
            text: "Un rendez-vous par téléphone ou en agence",
            callbackValue: "Un rendez-vous par téléphone ou en agence",
          },
          { text: "Autre", callbackValue: "Autre" },
        ]}
      />
      <Textarea id="message" label="Votre message*" name="message" control={control} />
      <div className="flex justify-center">
        <PrimaryButton disabled={status === "executing"} type="submit" direction="up">
          Envoyer
        </PrimaryButton>
      </div>
      {status === "executing" && (
        <div>
          <Spinner />
        </div>
      )}
      {result.data?.message && (
        <div className="flex justify-center text-[#63C8B6] font-medium">{result.data.message}</div>
      )}
      <p className="text-primary text-xs">
        Les données personnelles collectées sont utilisées par Intermèdes afin de traiter vos demandes au Service
        Clients et pour l&apos;exercice de vos droits en matière de données à caractère personnel. Vous disposez un
        droit d’accès, de modification, de suppression, de limitation et de portabilité de vos données. Consultez
        <Link href="/entreprise/donnees-et-garanties/" className="no-underline text-primary font-medium">
          {" notre politique de protection des données personnelles."}
        </Link>
      </p>
    </form>
  );
};
